import SingleRecipientEmailPanel from '@/components/email-composer/SingleRecipientEmailPanel';
import {
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { useEmailComposer } from '@/hooks/use-email-composer';
import { Box, Text } from '@chakra-ui/react';
import {
  InvitationStatus,
  type InvitationOutreachKind,
  type ProgramInvitation,
} from '@fluenceur/types';
import { useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

interface InvitationOutreachEmailModalProps {
  invitation: ProgramInvitation | null;
  /** `follow_up` from the resend action, `outreach` (a plain email) from the envelope. */
  kind: InvitationOutreachKind;
  isOpen: boolean;
  onClose: () => void;
}

const InvitationOutreachEmailModal: FC<InvitationOutreachEmailModalProps> = ({
  invitation,
  kind,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const recipientName = invitation?.invitee.name || invitation?.invitee.email || '';
  const recipientEmail = invitation?.invitee.email || '';
  // Sending renews an expired invitation before the email leaves, so its link
  // is live again by the time the invitee reads it. Only a follow-up says so:
  // a plain email renews it only when it carries the link.
  const isExpired = invitation?.status === InvitationStatus.EXPIRED;
  const hasInvitationLink =
    invitation?.status === InvitationStatus.PENDING ||
    invitation?.status === InvitationStatus.CLICKED ||
    isExpired;
  const showsRenewalNote = kind === 'follow_up' && isExpired;
  const mode = useMemo(
    () => ({
      type: 'invitation-outreach' as const,
      kind,
      invitationId: invitation?.id ?? '',
      hasCommissionRate: invitation?.program.hasCommissionRate ?? false,
      hasInvitationLink,
      previewInviteeName: recipientName,
      previewProgramName: invitation?.program.name,
    }),
    [
      hasInvitationLink,
      kind,
      invitation?.id,
      invitation?.program.hasCommissionRate,
      invitation?.program.name,
      recipientName,
    ]
  );

  const composer = useEmailComposer({
    hasProgramContext: false,
    mode,
    onClose,
  });

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => !e.open && composer.handleClose()}
      closeOnInteractOutside={false}
      size={composer.isConnected ? 'xl' : 'md'}
      placement="center"
    >
      <DialogContent overflow="hidden">
        <DialogHeader pb={3}>
          <DialogTitle fontSize="lg" fontWeight="bold">
            {kind === 'follow_up'
              ? t('invitationOutreach.panel.title')
              : t('invitationOutreach.panel.outreachTitle')}
          </DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>

        <Box px={4} pb={4}>
          <SingleRecipientEmailPanel
            composer={composer}
            title=""
            recipientLabel={t('invitationOutreach.panel.recipient')}
            recipientName={recipientName}
            recipientEmail={recipientEmail}
            recipientCaption={
              showsRenewalNote ? (
                <Text fontSize="xs" color="fg.muted">
                  {t('invitations.bulkEmail.renewalNoteSingle')}
                </Text>
              ) : undefined
            }
            confirmTitle={t('invitationOutreach.panel.confirm.title')}
            confirmDescription={t('invitationOutreach.panel.confirm.description', {
              name: recipientName,
              email: composer.senderEmail,
            })}
            confirmBackLabel={t('invitationOutreach.panel.confirm.backToEdit')}
            confirmSendLabel={t('invitationOutreach.panel.confirm.send')}
            sendLabel={t('invitationOutreach.panel.send')}
            variant="modal"
          />
        </Box>
      </DialogContent>
    </DialogRoot>
  );
};

export default InvitationOutreachEmailModal;
